import { useState, useEffect } from 'react';
import type { Item } from '../lib/types.ts';
import { api } from '../lib/api.ts';
import { ScoreBadge } from '../components/ScoreBadge.tsx';
import { ClassificationBadge } from '../components/ClassificationBadge.tsx';

interface Props {
  itemId: string;
  onBack: () => void;
}

type Angle = { headline?: string; why_now?: string; suggested_visual?: string };
type Anomaly = { description?: string; significance?: string };
type ComparableContext = { context?: string };

function parseJson<T>(raw: unknown): T | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'object') return raw as T;
  if (typeof raw === 'string') {
    try { return JSON.parse(raw) as T; } catch { return null; }
  }
  return null;
}

function fmtDate(ts: number | null | undefined): string {
  if (!ts) return '—';
  return new Date(ts * 1000).toLocaleDateString('en-IE', { day: 'numeric', month: 'long', year: 'numeric' });
}

function fmtMetricValue(v: unknown): string {
  if (typeof v === 'number') {
    if (v > 999999) return `€${(v / 1_000_000).toFixed(1)}m`;
    if (v > 9999) return `€${Math.round(v / 1000)}k`;
    if (v > 999) return v.toLocaleString('en-IE');
    return String(v);
  }
  return String(v ?? '');
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function Tag({ label, color = '#eee', textColor = '#555' }: { label: string; color?: string; textColor?: string }) {
  return (
    <span style={{
      display: 'inline-block', background: color, color: textColor,
      borderRadius: 3, padding: '2px 8px', fontSize: 11, marginRight: 4, marginBottom: 4,
    }}>
      {label}
    </span>
  );
}

export function ItemDetail({ itemId, onBack }: Props) {
  const [item, setItem] = useState<Item | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [acted, setActed] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    api.items.get(itemId)
      .then(setItem)
      .finally(() => setLoading(false));
  }, [itemId]);

  async function act(status: string) {
    if (!item) return;
    setBusy(true);
    try {
      await api.items.setStatus(item.id, status);
      setActed(status);
    } finally {
      setBusy(false);
    }
  }

  if (loading) return (
    <div style={{ padding: 40, color: '#999', textAlign: 'center' }}>Loading…</div>
  );
  if (!item) return (
    <div style={{ padding: 40, color: '#c00' }}>Item not found.</div>
  );

  const angles = parseJson<Angle[]>(item.deep_story_angles) ?? [];
  const anomalies = parseJson<Anomaly[]>(item.deep_data_anomalies) ?? [];
  const comparable = parseJson<ComparableContext[]>(item.deep_comparable_stories) ?? [];
  const metrics = parseJson<Record<string, unknown>>(item.key_metrics);
  const metricEntries = metrics
    ? Object.entries(metrics).filter(([, v]) => v !== null && v !== undefined && v !== '')
    : [];
  const tags = parseJson<string[]>(item.topic_tags) ?? [];
  const resources = parseJson<{ format: string; url: string }[]>(item.resource_urls) ?? [];

  const classification = (item.deep_final_classification ?? item.triage_type ?? null) as
    'ARTICLE' | 'CHART' | 'BRIEF' | 'MONITOR' | 'NOISE' | null;

  const headline = angles[0]?.headline ?? item.title ?? '—';

  return (
    <div>
      {/* Back nav */}
      <button onClick={onBack} style={{
        background: 'none', border: 'none', color: '#1a6aff', cursor: 'pointer',
        fontSize: 13, padding: 0, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 4,
      }}>
        ← Back to Inbox
      </button>

      {/* Header */}
      <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 8, padding: '24px 28px', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          <ClassificationBadge classification={classification} />
          <ScoreBadge score={item.deep_final_score ?? item.triage_score} />
          <span style={{ fontSize: 12, color: '#999', textTransform: 'uppercase' }}>
            {(item.source_name ?? item.source_id).replace(/_/g, ' ')}
          </span>
          <span style={{ marginLeft: 'auto', fontSize: 12, color: '#bbb' }}>
            {fmtDate(item.published_at ?? item.ingested_at)}
          </span>
        </div>

        <h1 style={{ margin: '0 0 14px', fontSize: 22, lineHeight: 1.3, color: '#111' }}>
          {headline}
        </h1>

        {item.deep_summary && (
          <p style={{ margin: '0 0 16px', fontSize: 15, color: '#333', lineHeight: 1.6 }}>
            {item.deep_summary}
          </p>
        )}

        {!item.deep_summary && item.description && (
          <p style={{ margin: '0 0 16px', fontSize: 14, color: '#555', lineHeight: 1.6 }}>
            {item.description}
          </p>
        )}

        {/* Key metrics */}
        {metricEntries.length > 0 && (
          <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', marginBottom: 16, padding: '14px 0', borderTop: '1px solid #f0f0f0', borderBottom: '1px solid #f0f0f0' }}>
            {metricEntries.map(([k, v]) => (
              <div key={k}>
                <div style={{ fontSize: 10, color: '#aaa', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>
                  {k.replace(/_/g, ' ')}
                </div>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#111' }}>
                  {fmtMetricValue(v)}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Action buttons */}
        {acted ? (
          <div style={{ fontSize: 13, color: '#27ae60', fontWeight: 600 }}>
            ✓ Marked as {acted}
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={() => act('flagged')} disabled={busy} style={btn('#1a6aff')}>
              Flag for editor
            </button>
            <button onClick={() => act('dismissed')} disabled={busy} style={btn('#999')}>
              Dismiss
            </button>
            <button onClick={() => act('published')} disabled={busy} style={btn('#27ae60')}>
              Mark published
            </button>
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20, alignItems: 'start' }}>
        {/* Main column */}
        <div>
          {/* Story angles */}
          {angles.length > 0 && (
            <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 8, padding: '20px 24px', marginBottom: 20 }}>
              <Section title="Story angles">
                {angles.map((a, i) => (
                  <div key={i} style={{
                    padding: '14px 0',
                    borderTop: i > 0 ? '1px solid #f0f0f0' : undefined,
                  }}>
                    <div style={{ fontWeight: 600, fontSize: 15, color: '#111', marginBottom: 6 }}>
                      {a.headline}
                    </div>
                    <div style={{ fontSize: 13, color: '#555', lineHeight: 1.5 }}>
                      <strong style={{ color: '#888' }}>Why now:</strong> {a.why_now}
                    </div>
                    {a.suggested_visual && (
                      <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                        <strong>Visual:</strong> {a.suggested_visual}
                      </div>
                    )}
                  </div>
                ))}
              </Section>
            </div>
          )}

          {/* Data anomalies */}
          {anomalies.length > 0 && (
            <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 8, padding: '20px 24px', marginBottom: 20 }}>
              <Section title="Data anomalies & notable patterns">
                {anomalies.map((a, i) => (
                  <div key={i} style={{ padding: '10px 0', borderTop: i > 0 ? '1px solid #f0f0f0' : undefined }}>
                    <div style={{ fontSize: 13, color: '#333', lineHeight: 1.5 }}>
                      {a.description}
                    </div>
                    {a.significance && (
                      <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                        <strong>Significance:</strong> {a.significance}
                      </div>
                    )}
                  </div>
                ))}
              </Section>
            </div>
          )}

          {/* Comparable context */}
          {comparable.length > 0 && comparable[0]?.context && (
            <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 8, padding: '20px 24px', marginBottom: 20 }}>
              <Section title="Comparable context">
                <p style={{ fontSize: 13, color: '#444', lineHeight: 1.6, margin: 0 }}>
                  {comparable[0].context}
                </p>
              </Section>
            </div>
          )}

          {/* Triage reason (fallback for non-analysed items) */}
          {!item.deep_summary && item.triage_reason && (
            <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 8, padding: '20px 24px', marginBottom: 20 }}>
              <Section title="Triage assessment">
                <p style={{ fontSize: 13, color: '#555', lineHeight: 1.6, margin: 0, fontStyle: 'italic' }}>
                  {item.triage_reason}
                </p>
              </Section>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div>
          {/* Source info */}
          <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 8, padding: '20px 24px', marginBottom: 16 }}>
            <Section title="Source">
              <div style={{ fontSize: 13, color: '#333', marginBottom: 8 }}>
                <strong>{item.publisher ?? item.source_name ?? item.source_id}</strong>
              </div>
              {item.geographic_scope && (
                <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>
                  <strong>Scope:</strong> {item.geographic_scope}
                </div>
              )}
              {item.data_period_start && (
                <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>
                  <strong>Period:</strong> {fmtDate(item.data_period_start)}
                  {item.data_period_end && ` – ${fmtDate(item.data_period_end)}`}
                </div>
              )}
              {resources.length > 0 && (
                <div style={{ marginTop: 10 }}>
                  {resources.map((r, i) => (
                    <a key={i} href={r.url} target="_blank" rel="noopener noreferrer"
                      style={{ display: 'block', fontSize: 12, color: '#1a6aff', marginBottom: 4, wordBreak: 'break-all' }}>
                      {r.format} — source data ↗
                    </a>
                  ))}
                </div>
              )}
              {item.raw_url && resources.length === 0 && (
                <a href={item.raw_url} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'block', fontSize: 12, color: '#1a6aff', marginTop: 10, wordBreak: 'break-all' }}>
                  Open source data ↗
                </a>
              )}
            </Section>
          </div>

          {/* Tags */}
          {tags.length > 0 && (
            <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 8, padding: '20px 24px', marginBottom: 16 }}>
              <Section title="Topics">
                <div>
                  {tags.map(t => <Tag key={t} label={t} color="#f0f4ff" textColor="#1a6aff" />)}
                </div>
              </Section>
            </div>
          )}

          {/* Status */}
          <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 8, padding: '20px 24px' }}>
            <Section title="Status">
              <div style={{ fontSize: 13, color: '#333' }}>
                <Tag
                  label={item.status}
                  color={item.status === 'analysed' ? '#e6f9ef' : item.status === 'flagged' ? '#e6f0ff' : '#f5f5f5'}
                  textColor={item.status === 'analysed' ? '#27ae60' : item.status === 'flagged' ? '#1a6aff' : '#666'}
                />
              </div>
              <div style={{ fontSize: 11, color: '#bbb', marginTop: 8 }}>
                Ingested {fmtDate(item.ingested_at)}
              </div>
            </Section>
          </div>
        </div>
      </div>
    </div>
  );
}

function btn(bg: string): React.CSSProperties {
  return {
    background: bg, color: '#fff', border: 'none', borderRadius: 4,
    padding: '7px 16px', fontSize: 13, cursor: 'pointer', fontWeight: 600,
  };
}
